import { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, AtSign, Linkedin, Youtube, Phone, Mail, MapPin, MessageCircle, Clock, Smartphone, Star } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { useContent } from "@/lib/queries";
import { api, errMsg } from "@/lib/api";
import { COMPANY } from "@/lib/data";

const ICONS = { Facebook, Instagram, Threads: AtSign, Linkedin, Youtube };
const COLS = [
  ["Quick Links", [["Book Service", "/book"], ["Track Booking", "/track"], ["Services", "/services"], ["Cities", "/cities"], ["Offers", "/offers"], ["Service Areas", "/service-areas"], ["PIN Code Finder", "/pincode-finder"]]],
  ["Company", [["About Us", "/about"], ["Become Partner", "/partner"], ["Careers", "/careers"], ["Franchise", "/franchise"], ["Corporate Services", "/corporate-services"], ["Refer & Earn", "/refer-and-earn"], ["Technician App", "/technician-app"], ["Blogs", "/blogs"]]],
  ["Support", [["Help Center", "/help"], ["Warranty", "/warranty"], ["Contact Us", "/contact"], ["Privacy Policy", "/privacy"], ["Terms & Conditions", "/terms"]]],
];
const PAY = ["Cash", "UPI", "Credit / Debit Card", "Net Banking"];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Thanks for joining.");
      setEmail("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="flex gap-2 max-w-sm" data-testid="newsletter-form">
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="flex-1 h-10 rounded-full bg-white/10 px-4 text-sm text-white placeholder:text-slate-400 outline-none focus:ring-2 ring-emerald-500" data-testid="newsletter-email" />
      <button type="submit" className="h-10 rounded-full bg-emerald-500 px-4 text-sm font-semibold text-[#08140E]" data-testid="newsletter-submit">Subscribe</button>
    </form>
  );
};

export const Footer = () => {
  const { data: c = {} } = useContent();
  return (
    <footer className="bg-[#08140E] text-slate-300 mt-24 pb-24 sm:pb-0" data-testid="site-footer">
      <div className="container-x py-12 grid gap-6 md:grid-cols-3 border-b border-white/10">
        <div><p className="text-white font-semibold mb-3">Newsletter</p><p className="text-sm mb-3">Offers and home-care tips, once in a while.</p><Newsletter /></div>
        <div data-testid="footer-app"><p className="text-white font-semibold mb-3">Download App</p>
          <div className="flex gap-2">{["Google Play", "App Store"].map((s) => <span key={s} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2 text-xs"><Smartphone className="h-4 w-4 text-emerald-400" /><span>{s}<br /><b className="text-white">Coming Soon</b></span></span>)}</div>
        </div>
        <div data-testid="footer-payments"><p className="text-white font-semibold mb-3">Payment Methods</p>
          <div className="flex flex-wrap gap-2">{PAY.map((p) => <span key={p} className="rounded-lg bg-white/5 border border-white/10 px-3 py-1.5 text-xs">{p}</span>)}</div>
          <p className="text-xs mt-2 text-slate-400">Pay after service is completed.</p>
        </div>
      </div>
      <div className="container-x py-14 grid gap-10 lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div className="space-y-5">
          <Logo className="h-9" testid="footer-logo" />
          <p className="text-sm leading-relaxed max-w-sm">Trusted, verified professionals for repairs, cleaning, installation and maintenance — at your doorstep, across India.</p>
          <div className="space-y-2 text-sm">
            <a href={COMPANY.phoneHref} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone"><Phone className="h-4 w-4 text-emerald-400" />{COMPANY.phone}</a>
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email"><Mail className="h-4 w-4 text-emerald-400" />{COMPANY.email}</a>
            <a href="https://www.google.com/maps/search/?api=1&query=28%2C+2nd+Main+Road%2C+Koramangala%2C+Bengaluru+560029" target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:text-white" data-testid="footer-map-link"><MapPin className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />28, 2nd Main Road, Koramangala, Bengaluru 560029 · View on Google Maps</a>
            {c.business_hours && <p className="flex items-center gap-2" data-testid="footer-hours"><Clock className="h-4 w-4 text-emerald-400" />{c.business_hours}</p>}
            {c.gst_number && <p className="text-xs" data-testid="footer-gst">GSTIN: {c.gst_number}</p>}
          </div>
          <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2 text-sm font-semibold" data-testid="footer-whatsapp"><MessageCircle className="h-4 w-4" />WhatsApp Support</a>
          <div className="flex gap-2">
            {COMPANY.social.map(({ name, href }) => {
              const I = ICONS[name];
              return <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Onkaal on ${name}`} data-testid={`footer-social-${name.toLowerCase()}`} className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-emerald-600 transition-colors"><I className="h-4 w-4" /></a>;
            })}
          </div>
        </div>
        {COLS.map(([title, links]) => (
          <div key={title}>
            <h3 className="text-white font-semibold mb-4">{title}</h3>
            <ul className="space-y-2.5 text-sm">{links.map(([l, to]) => <li key={to}><Link to={to} className="hover:text-emerald-400 transition-colors" data-testid={`footer-link-${to.slice(1)}`}>{l}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col sm:flex-row gap-2 justify-between text-xs text-slate-400">
          <p data-testid="footer-copyright">© 2026 Onkaal.com. All rights reserved.</p>
          <p className="flex items-center gap-1"><Star className="h-3 w-3 text-amber-400 fill-current" />Founded 2026 · Bengaluru · Hyderabad</p>
        </div>
      </div>
    </footer>
  );
};
